import * as d3 from "d3";
import renderSegmentLabel, {Icoord} from "./SegmentLabel";
import {SidebarTab} from "../../Sidebar";
import {ITypeRadar} from "../../../types";
import {ICircleRadiusItem, xScale} from "../../../mocks/projectsMock";

interface SegmentLabelsProps {
  mainGroup: d3.Selection<SVGGElement, unknown, null, undefined>;
  segmentLabels: string[];
  quantitySections: number;
  colors: {back: string; color: string}[];
  fourthCircleRadius: ICircleRadiusItem;
  firstCircleRadius: ICircleRadiusItem;
  isWeak_signal?: boolean;
  activeDirectionId: number | string | null | undefined;
  directions: any;
  openSidebarWithTab: (
    tab: SidebarTab,
    directionId: number,
    clussterId?: number,
    name?: any,
    sectionName?: any,
    colorTitle?: {back: string; color: string}
  ) => void;
  typeRadar: ITypeRadar;
  isNationalProjects?: boolean;
  setCoordinatesDirectionActive: (position: Icoord[]) => void;
  coordinatesDirectionActive?: Icoord[];
}

const renderSegmentLabels = ({
  mainGroup,
  segmentLabels,
  quantitySections,
  colors,
  fourthCircleRadius,
  firstCircleRadius,
  isWeak_signal,
  activeDirectionId,
  directions,
  openSidebarWithTab,
  typeRadar,
  isNationalProjects,
  setCoordinatesDirectionActive,
  coordinatesDirectionActive,
}: SegmentLabelsProps) => {
  const segmentAngles =
    quantitySections === 0
      ? d3.range(-90, 360, 0)
      : d3.range(-90, 360, 360 / quantitySections);
  const labelAngles = segmentAngles.map(
    (angle) => angle + 360 / quantitySections / 2
  );

  let prevNptlId = 0;

  let prevStartAngle = 0;
  let prevEndAngle = 0;
  let startLineX = 0;
  let startLineY = 0;
  let startI = 0;
  let num = 1;

  let coincidence = false;
  // if (!isWeak_signal) {
  segmentAngles.forEach((angle, i) => {
    let isNew = false;
    if (prevNptlId !== directions[i]?.nptl?.id) {
      prevNptlId = directions[i]?.nptl?.id;
      isNew = true;
    }
    const startX = Math.cos((angle * Math.PI) / 180) * firstCircleRadius.radius;
    const startY = Math.sin((angle * Math.PI) / 180) * firstCircleRadius.radius;
    const endX = Math.cos((angle * Math.PI) / 180) * fourthCircleRadius.radius;
    const endY = Math.sin((angle * Math.PI) / 180) * fourthCircleRadius.radius;
    const lengthMarker = 15;
    const endX2 =
      Math.cos(((angle + 1) * Math.PI) / 180) *
      (fourthCircleRadius.radius + lengthMarker);
    const endY2 =
      Math.sin(((angle + 1) * Math.PI) / 180) *
      (fourthCircleRadius.radius + lengthMarker);

    mainGroup
      .append("line")
      .attr("x1", startX)
      .attr("y1", startY)
      .attr("x2", endX)
      .attr("y2", endY)
      .style("stroke", "#11A0DA")
      .style("stroke-width", 1)
      .style("opacity", isNationalProjects ? 0.3 : 1);

    if (isNationalProjects) {
      const backColor =
        directions[i]?.nptl?.id === 6
          ? "#69C303"
          : directions[i]?.nptl?.id === 2
          ? "#00F0FF"
          : directions[i]?.nptl?.id === 4
          ? "#FFA726"
          : directions[i]?.nptl?.id === 13
          ? "#BF3535"
          : directions[i]?.nptl?.id === 11
          ? "#FF00F2"
          : directions[i]?.nptl?.id === 8
          ? "#59FFAC"
          : "#11A0DA";

      const startAngle = (angle * Math.PI) / 180 + Math.PI / 2;
      const endAngle =
        ((segmentAngles[i + 1] ?? 360 + segmentAngles[0]) * Math.PI) / 180 +
        Math.PI / 2;
      if (isNew) {
        prevStartAngle = startAngle;
        prevEndAngle = endAngle;
        num = 1;
        startI = i;
        startLineX = endX2;
        startLineY = endY2;

        coincidence = false;
      } else {
        prevEndAngle = prevEndAngle + (endAngle - startAngle);
        num++;
      }
      if (directions[i]?.nptl?.name === directions[i]?.name) coincidence = true;
      const arc = d3
        .arc()
        .innerRadius(firstCircleRadius.radius)
        .outerRadius(fourthCircleRadius.radius)
        .startAngle(prevStartAngle)
        .endAngle(prevEndAngle);

      const gradient = mainGroup
        .append("linearGradient")
        .attr("id", `gradient-${i}`)
        .attr("x1", "0%")
        .attr("x2", "0%")
        .attr("y1", "0%")
        .attr("y2", "100%");

      gradient
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", backColor)
        .attr("stop-opacity", 0);

      gradient
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", backColor)
        .attr("stop-opacity", 0.35);
      if (directions[i]?.nptl?.id !== directions[i + 1]?.nptl?.id) {
        const arrActiveDirection = directions.filter(
          (direction: any) => direction.nptl.id === directions[i]?.nptl?.id
        );
        mainGroup
          .append("path")
          .attr(
            "d",
            arc({
              innerRadius: firstCircleRadius.radius,
              outerRadius: fourthCircleRadius.radius,
              startAngle: prevStartAngle,
              endAngle: prevEndAngle,
            })
          )
          .attr("fill", `url(#gradient-${i})`)
          .attr(
            "stroke",
            activeDirectionId &&
              arrActiveDirection.some(
                (dir: any) => dir.id === activeDirectionId
              )
              ? backColor
              : "#11A0DA"
          )
          .style("stroke-width", 1)
          .attr(
            "opacity",
            activeDirectionId &&
              arrActiveDirection.some(
                (dir: any) => dir.id === activeDirectionId
              )
              ? 1
              : activeDirectionId
              ? 0.2
              : 1
          );

        const endX2 =
          Math.cos(((segmentAngles[startI + num] - 1) * Math.PI) / 180) *
          (fourthCircleRadius.radius + lengthMarker);
        const endY2 =
          Math.sin(((segmentAngles[startI + num] - 1) * Math.PI) / 180) *
          (fourthCircleRadius.radius + lengthMarker);

        mainGroup
          .append("path")
          .attr("d", () => {
            const radius = fourthCircleRadius.radius + lengthMarker;
            return `M ${startLineX} ${startLineY} A ${radius} ${radius} 0 0 1 ${endX2} ${endY2}`;
          })
          // .style("stroke", "#11A0DA")
          .style("stroke-width", 10)
          .style("stroke", backColor)
          .style(
            "opacity",
            activeDirectionId &&
              arrActiveDirection.some(
                (dir: any) => dir.id === activeDirectionId
              )
              ? 0.8
              : activeDirectionId
              ? 0.1
              : 0.3
          )
          .style("fill", "none");

        const sColor = {
          back: "#aaaaaa",
          color: "#ffffff",
        };
        if (!coincidence)
          renderSegmentLabel({
            mainGroup,
            angle,
            label: directions[i]?.nptl?.name,
            radius: fourthCircleRadius.radius,
            segmentColor: sColor,
            isActiveDirection: activeDirectionId === directions[i]?.id,
            openSidebarWithTab,
            directions,
            segmentIndex: i,
            activeDirectionId,
            typeRadar,
            isWeak_signal,
            isNationalProjects,
            backColor,
            isNPTL: true,
            isBtn: false,
          });
      }
    }
  });
  const arrBackColor = [
    "#69C303",
    "#00F0FF",
    "#FFA726",
    "#BF3535",
    "#FF00F2",
    "#59FFAC",
  ];

  arrBackColor.forEach((color, i) => {});
  // }

  labelAngles.forEach((angle, i) => {
    const segmentLabel = segmentLabels[i];
    const segmentColor = colors[i % colors.length];
    // const innerColor = segmentColor.replace(/0.5\)$/, "1)");
    const backColor =
      directions[i]?.nptl?.id === 6
        ? "#69C303"
        : directions[i]?.nptl?.id === 2
        ? "#00F0FF"
        : directions[i]?.nptl?.id === 4
        ? "#FFA726"
        : directions[i]?.nptl?.id === 13
        ? "#BF3535"
        : directions[i]?.nptl?.id === 11
        ? "#FF00F2"
        : directions[i]?.nptl?.id === 8
        ? "#59FFAC"
        : "#11A0DA";

    if (segmentLabel) {
      // if (!isWeak_signal) {
      renderSegmentLabel({
        mainGroup,
        angle,
        label: segmentLabel,
        radius: fourthCircleRadius.radius,
        segmentColor,
        isActiveDirection: activeDirectionId === directions[i]?.id,
        openSidebarWithTab,
        directions,
        segmentIndex: i,
        activeDirectionId,
        typeRadar,
        isWeak_signal,
        isNationalProjects,
        backColor,
        isNPTL: directions[i]?.nptl?.name === directions[i]?.name,
        isBtn: true,
        setCoordinatesDirectionActive,
        coordinatesDirectionActive,
      });
      // }
    }
  });

  return null;
};

export default renderSegmentLabels;
